import { defaultQueryOptions } from 'constants/'
import { useQuery, UseQueryResult } from 'react-query'
import type { QueryOptions } from 'types'
import { logFactory } from 'utils'
import { useConsultantService, Consultant } from '../services'

const hookName = 'useConsultant'
const log = logFactory(hookName)

interface UseConsultantProps {
  consultantGid?: string
  consultantSalesforceId?: string
  options?: QueryOptions
}

const useConsultant = ({
  consultantGid,
  consultantSalesforceId,
  options = { enabled: true },
}: UseConsultantProps): UseQueryResult<Consultant, unknown> => {
  const { searchConsultants } = useConsultantService()

  const doQuery = async () => {
    if (!consultantGid && !consultantSalesforceId) {
      throw new Error('Either consultant GID or Salesforce ID is required')
    }

    log(`👨‍💼 Fetching consultant...`, { consultantGid, consultantSalesforceId })

    if (consultantGid || consultantSalesforceId) {
      const { data } = await searchConsultants({
        gid: (consultantGid && [consultantGid]) || undefined,
        salesforceId: (consultantSalesforceId && [consultantSalesforceId]) || undefined,
      })
      const [consultant] = data ?? ([] as Consultant[])

      log(`👨‍💼 Found consultant by id`, consultant)

      return consultant
    }

    throw new Error('Consultant not found')
  }

  return useQuery([hookName, consultantGid, consultantSalesforceId], doQuery, {
    ...defaultQueryOptions,
    ...options,
    enabled: Boolean(consultantGid || consultantSalesforceId) && options.enabled,
  })
}

export { useConsultant }
